import {isAllOf} from '@reduxjs/toolkit';

import {TData, TPayload} from 'src/components/List';
import {TItemId} from 'src/constants';
import {
  CACHE_LIFETIME,
  ECacheTag,
  isMatchQueryMeta,
  listenerMiddleware,
  rootApi,
  showErrorNotification,
} from 'src/models';
import {booleanHashToArray} from 'src/utils';

import {showDeniedDeletionModal} from './DeniedDeletionModal';
import {ESmsCategory, TCategoryOperator} from './SMSForm/definitions';
import {TExamplesPayload} from './WhatsAppForm/definitions';
import {
  TCheckPatternMatch,
  TFilters,
  TFormData,
  TMessageMatcher,
  TSupportedLanguages,
  TValidationResult,
} from './definitions';
import {prepareDataForSave} from './utils';

export const prepareFilters = ({period, ...payload}: TFilters) => ({
  ...payload,
  timeFrom: period?.startDate,
  timeTo: period?.stopDate,
  statuses: booleanHashToArray(payload.statuses),
  types: booleanHashToArray(payload.types),
  channelTypes: booleanHashToArray(payload.channelTypes),
});

const api = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getMessageMatchers: builder.query<
      TData<TMessageMatcher>,
      TPayload<TFilters & {tenantId?: string}>
    >({
      query: ({tenantId, ...data}) => ({
        method: 'POST',
        url: !!tenantId ? '/message-matcher/support/list' : '/message-matcher/list',
        data: prepareFilters(data),
        params: {tenantId},
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.MESSAGE_MATCHERS];
        }

        return [
          ECacheTag.MESSAGE_MATCHERS,
          ECacheTag.MESSAGE_MATCHERS_LIST,
          ...result.content.map(({id}) => ({id, type: ECacheTag.MESSAGE_MATCHERS})),
        ];
      },
    }),
    getMessageMatcher: builder.query<TMessageMatcher, {id: TItemId; tenantId?: TItemId}>({
      query: ({id, tenantId}) => ({
        method: 'GET',
        url: !!tenantId ? `/message-matcher/support/${id}` : `/message-matcher/${id}`,
        params: {tenantId},
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToLoadItem',
        },
      }),
      providesTags: (_, __, {id}) => [{id, type: ECacheTag.MESSAGE_MATCHERS}],
    }),
    saveMessageMatcher: builder.mutation<TMessageMatcher, Partial<TFormData>>({
      query: (data) => ({
        method: data.id ? 'PUT' : 'POST',
        url: `/message-matcher/${data.id ?? ''}`,
        data: prepareDataForSave(data),
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToSaveItem',
        },
      }),
      invalidatesTags: (_, __, data) =>
        data.id ? [{id: data.id, type: ECacheTag.MESSAGE_MATCHERS}] : [],
    }),
    registerMessageMatcher: builder.mutation<TMessageMatcher, TItemId>({
      query: (id) => ({
        method: 'POST',
        url: `/message-matcher/${id}/register`,
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToRegisterItem',
        },
      }),
      invalidatesTags: (_, __, id) => [{id, type: ECacheTag.MESSAGE_MATCHERS}],
    }),
    registerMessageMatcherWithExamples: builder.mutation<TValidationResult, TExamplesPayload>({
      query: ({id, ...data}) => ({
        method: 'POST',
        url: `/message-matcher/${id}/validate-and-register`,
        data,
        meta: {isShowError: true, errorMessageKey: 'List:errors.failedToRegisterItem'},
      }),
      invalidatesTags: (_, __, {id}) => [{id, type: ECacheTag.MESSAGE_MATCHERS}],
    }),
    archiveMessageMatcher: builder.mutation<void, TItemId>({
      query: (id) => ({
        method: 'DELETE',
        url: `/message-matcher/archive/with-id/${id}`,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'List:notification.archiveSuccess',
        },
      }),
      invalidatesTags: [ECacheTag.MESSAGE_MATCHERS],
    }),
    deleteMessageMatcher: builder.mutation<void, TItemId>({
      query: (id) => ({
        method: 'DELETE',
        url: `/message-matcher/with-id/${id}`,
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToDeleteItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.deleteSuccess',
        },
      }),
      invalidatesTags: [ECacheTag.MESSAGE_MATCHERS_LIST],
    }),
    approveMessageMatcher: builder.mutation<TMessageMatcher, Partial<TFormData>>({
      query: (data) => ({
        method: 'POST',
        url: `/message-matcher/${data.id}/approve`,
        data: prepareDataForSave(data),
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToSaveItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
        },
      }),
      invalidatesTags: (_, __, data) => [{id: data.id, type: ECacheTag.MESSAGE_MATCHERS}],
    }),
    getSmsOperators: builder.query<
      {type: ESmsCategory; providerConfigs: TCategoryOperator[]}[],
      number
    >({
      query: (subjectId) => ({
        method: 'GET',
        url: '/message-matcher/sms/categories',
        params: {
          subjectId,
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getSupportedLanguages: builder.query<TSupportedLanguages, void>({
      query: () => ({
        method: 'GET',
        url: `/message-matcher/supported-languages`,
        meta: {isShowError: true},
      }),
    }),
    getMessageMatcherChildren: builder.query<TMessageMatcher[], TItemId>({
      query: (id) => ({
        method: 'GET',
        url: `/message-matcher/${id}/children`,
        meta: {isShowError: true, errorMessageKey: 'List:errors.failedToLoadItem'},
      }),
    }),
    checkForUniqueName: builder.mutation<
      {existing: boolean},
      {channelType: TMessageMatcher['channelType']; name: TMessageMatcher['name']}
    >({
      query: ({channelType, name}) => ({
        method: 'GET',
        url: `/message-matcher/exists-by/channel-type/${channelType}/name/${name}`,
        meta: {isShowError: true},
      }),
    }),
    checkForUniqueWabaName: builder.mutation<
      {existing: boolean},
      {subjectId: number; language: string; name: string}
    >({
      query: (data) => ({
        method: 'POST',
        url: '/message-matcher/exists-by-waba-id-name-language',
        data,
        meta: {isShowError: true},
      }),
    }),
    checkPatternMatch: builder.mutation<{match: boolean}, TCheckPatternMatch>({
      query: (data) => ({
        method: 'POST',
        url: `/message-matcher/pattern-match`,
        data,
        meta: {isShowError: true},
      }),
    }),
    findTemplateMatch: builder.mutation<TMessageMatcher, {subjectId: number; text: string}>({
      query: (data) => ({
        method: 'POST',
        url: `/message-matcher/by-matching-text`,
        data,
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: isAllOf(
    api.endpoints.archiveMessageMatcher.matchRejected,
    isMatchQueryMeta({isBackendError: true}),
  ),
  effect: ({payload}) => {
    if (payload.data.title === 'message-matcher-used-in-broadcast') {
      showDeniedDeletionModal(JSON.parse(payload.data.detail));
    } else {
      showErrorNotification(payload);
    }
  },
});

export const {
  useGetMessageMatchersQuery,
  useGetMessageMatcherQuery,
  useLazyGetMessageMatcherQuery,
  useLazyGetMessageMatcherChildrenQuery,

  useSaveMessageMatcherMutation,
  useApproveMessageMatcherMutation,
  useRegisterMessageMatcherMutation,
  useRegisterMessageMatcherWithExamplesMutation,
  useArchiveMessageMatcherMutation,
  useDeleteMessageMatcherMutation,

  useCheckPatternMatchMutation,
  useFindTemplateMatchMutation,
  useCheckForUniqueNameMutation,
  useCheckForUniqueWabaNameMutation,

  useGetSmsOperatorsQuery,
  useGetSupportedLanguagesQuery,
} = api;
